/**
 * Gift Message to Order Notes
 * 
 * This script collects all "Gift Message" line item properties from the cart
 * and adds them to the cart note field, which becomes the order note in Shopify.
 * This allows Royal Mail Click & Drop to access the gift messages.
 */

class GiftMessageCollector {
  constructor() {
    this.init();
  }

  async init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  async setup() {
    // Update gift messages when cart loads/updates
    await this.updateGiftMessagesToNote();

    // Listen for cart updates - theme event with multiple retries to avoid race condition
    document.addEventListener('theme:cart:update', () => {
      // Multiple checks at increasing intervals to catch cart sync
      setTimeout(() => this.updateGiftMessagesToNote(), 200);
      setTimeout(() => this.updateGiftMessagesToNote(), 500);
      setTimeout(() => this.updateGiftMessagesToNote(), 1000);
      setTimeout(() => this.updateGiftMessagesToNote(), 2000);
      setTimeout(() => this.updateGiftMessagesToNote(), 3000); // Final check
    });

    // Listen for cart drawer opening
    document.addEventListener('theme:cart:open', () => {
      setTimeout(() => this.updateGiftMessagesToNote(), 300);
      setTimeout(() => this.updateGiftMessagesToNote(), 1000);
    });

    // Listen for product added to cart
    document.addEventListener('theme:product:added', () => {
      setTimeout(() => this.updateGiftMessagesToNote(), 500);
      setTimeout(() => this.updateGiftMessagesToNote(), 2000);
    });

    // Attach checkout button listeners
    this.attachFormListeners();

    // Re-attach listeners when cart updates (for dynamically loaded buttons)
    const observer = new MutationObserver(() => {
      this.attachFormListeners();
    });
    
    // Observe cart changes
    const cartElements = document.querySelectorAll('[data-cart-form], [data-cart-drawer-inner]');
    cartElements.forEach(element => {
      if (element) {
        observer.observe(element, { childList: true, subtree: true });
      }
    });
  }

  attachFormListeners() {
    // Find checkout buttons and intercept clicks BEFORE form submission
    const checkoutButtons = document.querySelectorAll('button[name="checkout"], .cart__checkout');
    
    checkoutButtons.forEach(button => {
      if (!button.hasAttribute('data-gift-message-listener')) {
        button.setAttribute('data-gift-message-listener', 'true');
        
        button.addEventListener('click', (e) => {
          // Don't prevent default - let checkout happen naturally
          // Just fire off a synchronous update using sendBeacon
          console.log('[Gift Messages] 🛒 Checkout clicked - sending final update...');
          
          this.sendFinalUpdateBeacon();
        }, { capture: true }); // Use capture to run before other handlers
      }
    });
  }

  sendFinalUpdateBeacon() {
    try {
      // Use XMLHttpRequest in synchronous mode (only way to guarantee completion)
      // This is deprecated but necessary for this use case
      const xhr = new XMLHttpRequest();
      xhr.open('GET', '/cart.js?t=' + Date.now(), false); // false = synchronous
      xhr.send();
      
      if (xhr.status === 200) {
        const cart = JSON.parse(xhr.responseText);
        const giftMessages = this.collectGiftMessages(cart.items);
        
        if (giftMessages.length > 0) {
          console.log('[Gift Messages] Found', giftMessages.length, 'message(s) - updating...');
          
          const formattedMessages = this.formatGiftMessages(giftMessages);
          const newNote = this.mergeNotes(cart.note || '', formattedMessages);
          
          // Send update synchronously
          const updateXhr = new XMLHttpRequest();
          updateXhr.open('POST', '/cart/update.js', false); // false = synchronous
          updateXhr.setRequestHeader('Content-Type', 'application/json');
          updateXhr.send(JSON.stringify({ note: newNote }));
          
          if (updateXhr.status === 200) {
            console.log('[Gift Messages] ✓ Update sent synchronously');
          } else {
            console.warn('[Gift Messages] ⚠️ Update failed:', updateXhr.status);
          }
        }
      }
    } catch (error) {
      console.error('[Gift Messages] Error during sync update:', error);
      // Don't block checkout even if update fails
    }
  }

  mergeNotes(existingNote, giftMessages) {
    // Remove any existing gift message section
    const cleanedNote = this.removeGiftMessagesFromNote(existingNote);
    
    // Add gift messages section
    const separator = cleanedNote.trim() ? '\n\n' : '';
    return cleanedNote.trim() + separator + '=== GIFT MESSAGES ===\n\n' + giftMessages;
  }

  removeGiftMessagesFromNote(note) {
    // Remove the gift messages section if it exists
    const giftMessageMarker = '=== GIFT MESSAGES ===';
    const index = note.indexOf(giftMessageMarker);
    
    if (index !== -1) {
      // Remove everything from the marker onwards
      return note.substring(0, index).trim();
    }
    
    return note;
  }

  async updateGiftMessagesToNote() {
    try {
      // Cache-bust to get fresh data
      const response = await fetch('/cart.js?t=' + Date.now());
      const cart = await response.json();
      
      // Get existing note content
      const existingNote = cart.note || '';
      
      // Collect all gift messages from line items
      const giftMessages = this.collectGiftMessages(cart.items);
      
      // Check if count has changed since last check
      const countChanged = this.lastKnownGiftMessageCount !== undefined && 
                          this.lastKnownGiftMessageCount !== giftMessages.length;
      
      if (giftMessages.length > 0) {
        // Update cart note with gift messages
        const formattedMessages = this.formatGiftMessages(giftMessages);
        const newNote = this.mergeNotes(existingNote, formattedMessages);
        
        if (countChanged) {
          console.log('[Gift Messages] Count changed from', this.lastKnownGiftMessageCount, 'to', giftMessages.length);
        }
        
        console.log('[Gift Messages] Found', giftMessages.length, 'gift message(s), updating order notes...');
        await this.updateCartNote(newNote);
        console.log('[Gift Messages] Successfully updated order notes');
        
        // Store the count for verification
        this.lastKnownGiftMessageCount = giftMessages.length;
        
        // If count just changed, schedule another check in case more changes are coming
        if (countChanged) {
          console.log('[Gift Messages] Scheduling follow-up check in 2 seconds...');
          setTimeout(() => this.updateGiftMessagesToNote(), 2000);
        }
      } else {
        // If no gift messages, keep existing note without gift message section
        const cleanedNote = this.removeGiftMessagesFromNote(existingNote);
        if (cleanedNote !== existingNote) {
          await this.updateCartNote(cleanedNote);
        }
        this.lastKnownGiftMessageCount = 0;
      }
    } catch (error) {
      console.error('[Gift Messages] Error updating gift messages:', error);
    }
  }

  collectGiftMessages(items) {
    const messages = [];
    
    items.forEach((item, index) => {
      if (item.properties) {
        // Look for Gift Message property (case-insensitive)
        const giftMessageProp = Object.keys(item.properties).find(key => 
          key.toLowerCase() === 'gift message'
        );
        
        if (giftMessageProp && item.properties[giftMessageProp]) {
          messages.push({
            lineNumber: index + 1,
            productTitle: item.product_title,
            variantTitle: item.variant_title,
            message: item.properties[giftMessageProp]
          });
        }
      }
    });
    
    return messages;
  }

  formatGiftMessages(messages) {
    if (messages.length === 0) return '';
    
    let formatted = '';
    
    messages.forEach((msg, index) => {
      if (index > 0) formatted += '\n\n---\n\n';
      
      formatted += `Gift Message for: ${msg.productTitle}`;
      if (msg.variantTitle) {
        formatted += ` - ${msg.variantTitle}`;
      }
      formatted += `\n${msg.message}`;
    });
    
    return formatted;
  }

  async updateCartNote(value) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note: value
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update cart note');
      }
      
      return await response.json();
    } catch (error) {
      console.error('[Gift Messages] Error updating cart note:', error);
    }
  }

}

// Initialize the gift message collector
new GiftMessageCollector();
